import React, { useState } from 'react';
import { X, HelpCircle } from 'lucide-react';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import { Button } from '@/components/ui/Button';

import { HelpWorkflow } from './HelpWorkflow';
import { HelpBestPractices } from './HelpBestPractices';
import { HelpLimitations } from './HelpLimitations';
import { HelpJobSearchWorkflow } from './HelpJobSearchWorkflow';
import { HelpJobSearchBestPractices } from './HelpJobSearchBestPractices';
import { HelpJobSearchLimitations } from './HelpJobSearchLimitations';
import { AppMode } from '@/hooks/useWorkspacesState';

interface HelpDialogProps {
  appMode?: AppMode;
}

export function HelpDialog({ appMode = 'tailor' }: HelpDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isJobSearch = appMode === 'job_search';

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(true)}
        aria-label="Help and Workflow"
        className="flex items-center gap-1.5 font-medium text-slate-700 dark:text-slate-400"
      >
        <HelpCircle className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        <span className="hidden sm:inline">Help & Workflow</span>
      </Button>

      <ResponsiveOverlay
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        variant="modal"
        className="w-full sm:max-w-3xl max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="flex items-center gap-2">
            <div className="bg-emerald-100 dark:bg-emerald-500/20 p-1.5 rounded-md">
              <HelpCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">How ResumePro Works</h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(false)}
            aria-label="Close Help Dialog"
            className="rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="overflow-y-auto p-6 flex flex-col gap-8">
          {isJobSearch ? (
            <>
              <HelpJobSearchWorkflow />
              <div className="h-px bg-slate-200 dark:bg-slate-800 w-full" />
              <HelpJobSearchBestPractices />
              <div className="h-px bg-slate-200 dark:bg-slate-800 w-full" />
              <HelpJobSearchLimitations />
            </>
          ) : (
            <>
              <HelpWorkflow />
              <div className="h-px bg-slate-200 dark:bg-slate-800 w-full" />
              <HelpBestPractices />
              <div className="h-px bg-slate-200 dark:bg-slate-800 w-full" />
              <HelpLimitations />
            </>
          )}
        </div>
        
        <div className="flex justify-end p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 shrink-0 sm:rounded-b-2xl">
          <Button
            variant="primary"
            onClick={() => setIsOpen(false)}
            aria-label="Got it, close dialog"
            className="w-full sm:w-auto dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            Got it
          </Button>
        </div>
      </ResponsiveOverlay>
    </>
  );
}
